<template>
  <div
    class="strip"
    ref="rootElement"
    @click="rotate"
    @mouseenter="clearTimer"
    @mouseleave="setTimer"
    :class="[{ h400: stripHeight === 400 }]"
  >
    <TransitionGroup name="list">
      <figure v-for="i in images" :key="i.name">
        <div class="wrapper">
          <img :src="i.src" onerror="this.src='/placeholder.webp'"/>
          <figcaption>
            <h2>{{ i.name }}</h2>
            <p v-if="i.description">{{ i.description }}</p>
          </figcaption>
        </div>
      </figure>
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
interface Image {
  name: string;
  src: string;
  description: string;
}

const store = useStore();
const images = ref<Image[]>([]);
const startIndex = ref(0);
const timer = ref<NodeJS.Timeout | null>(null);
const stripHeight = ref(400);
const rootElement = ref<HTMLDivElement>();
const props = defineProps({
  max: {
    type: Number,
    default: 6,
  },
});

onMounted(() => {
  setImages();
  setTimer();
  addEventListener("resize", updateStripHeight);
});

watch(() => store.recipes, setImages);

onUnmounted(() => {
  clearTimer();
  removeEventListener("resize", updateStripHeight);
});

function setTimer() {
  if (timer.value) clearInterval(timer.value);
  timer.value = setInterval(rotate, 5000);
}

function clearTimer() {
  if (timer.value) clearInterval(timer.value);
}

function rotate() {
  if (!store.tag) return;
  if(props.max > store.recipes.length) return;
  if (startIndex.value < store.recipes.length - 1) startIndex.value++;
  else startIndex.value = 0;

  let nextIndex = startIndex.value + props.max;
  if (nextIndex > store.recipes.length - 1) {
    nextIndex = nextIndex - store.recipes.length;
  }

  if (rootElement.value) {
    let figures = rootElement.value.querySelectorAll("figure");
    figures.forEach((figure: HTMLElement) => {
      figure.style.top = figure.offsetTop + "px";
      figure.style.left = figure.offsetLeft + "px";
    });
  }

  let recipe = store.recipes[nextIndex];
  if (recipe.image) {
    let image = {
      name: recipe.name,
      src: getThumbsLink(recipe.image, store.tag),
      description: recipe.description || "",
    };
    let randomIndex = Math.floor(Math.random() * images.value.length);
    images.value.splice(randomIndex, 1, image);
    // images.value.shift();
    // images.value.push(image);
  }
}

function setImages() {
  if (!store.recipes.length || !store.tag) return;
  images.value = [];
  let currentIndex = startIndex.value;
  for (let i = 0; i < props.max; i++) {
    let recipe = store.recipes[currentIndex];
    let image = {
      name: recipe.name,
      src: getThumbsLink(recipe.image, store.tag),
      description: recipe.description || "",
    };
    images.value.push(image);
    if (currentIndex < store.recipes.length - 1) currentIndex++;
    else currentIndex = 0;
  }
}

function updateStripHeight() {
  let strip = document.querySelector(".strip");
  if (strip) {
    let height = strip.clientHeight;
    if (height) stripHeight.value = height;
  }
}
</script>

<style scoped>
.strip {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  position: relative;
  max-width: 960px;
}

figure {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  overflow: hidden;
  width: 310px;
  height: 200px;
  background: #000000;
  text-align: left;
  margin: 0px;
  opacity: 1;
  transition: opacity 0.3s ease, transform 1s ease;
}

figure * {
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
}

figure .wrapper {
  position: relative;
  width: 310px;
  height: 200px;
  overflow: hidden;
}

figure img {
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
  opacity: 1;
  width: 100%;
  -webkit-transition: opacity 0.35s, -webkit-transform 0.5s;
  transition: opacity 0.35s, transform 0.5s;
}

figure:hover img {
  opacity: 0.3;
  transform: scale(1.2);
}

figure figcaption {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 310px;
  height: 200px;
  padding: 30px 3em;
}

figure figcaption::before {
  position: absolute;
  top: 30px;
  right: 30px;
  bottom: 30px;
  left: 100%;
  border-left: 4px solid rgba(255, 255, 255, 0.8);
  content: "";
  opacity: 0;
  background-color: rgba(255, 255, 255, 0.5);
  -webkit-transition: all 0.5s;
  transition: all 0.5s;
  -webkit-transition-delay: 0.6s;
  transition-delay: 0.6s;
}

figure h2,
figure p {
  margin: 0 0 5px;
  opacity: 0;
  -webkit-transition: opacity 0.35s, -webkit-transform 0.35s;
  transition: opacity 0.35s, -webkit-transform 0.35s, -moz-transform 0.35s,
    -o-transform 0.35s, transform 0.35s;
}

figure h2 {
  word-spacing: -0.15em;
  font-weight: 300;
  text-transform: uppercase;
  -webkit-transform: translate3d(30%, 0%, 0);
  transform: translate3d(30%, 0%, 0);
  -webkit-transition-delay: 0.3s;
  transition-delay: 0.3s;
}

figure h2 span {
  font-weight: 800;
}

figure p {
  font-weight: 200;
  -webkit-transform: translate3d(0%, 30%, 0);
  transform: translate3d(0%, 30%, 0);
  -webkit-transition-delay: 0s;
  transition-delay: 0s;
}

figure a {
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  position: absolute;
  color: #ffffff;
}

figure:hover img {
  opacity: 0.3;
}

figure:hover figcaption h2 {
  opacity: 1;
  -webkit-transform: translate3d(0%, 0%, 0);
  transform: translate3d(0%, 0%, 0);
  -webkit-transition-delay: 0.4s;
  transition-delay: 0.4s;
}

figure:hover figcaption p {
  opacity: 0.9;
  -webkit-transform: translate3d(0%, 0%, 0);
  transform: translate3d(0%, 0%, 0);
  -webkit-transition-delay: 0.6s;
  transition-delay: 0.6s;
}

figure:hover figcaption::before {
  background: rgba(255, 255, 255, 0);
  left: 30px;
  opacity: 1;
  -webkit-transition-delay: 0s;
  transition-delay: 0s;
}

/* list transition */
.list-move,
.list-enter-active,
.list-leave-active {
  opacity: 0.8;
  transition: opacity 0.8s ease, transform 0.8s ease;
}

.list-leave-to {
  opacity: 0;
  transform: rotateY(180deg);
}

.list-enter-from {
  opacity: 0;
  transform: rotateY(180deg);
}

/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
.list-leave-active {
  position: absolute;
}

@media screen and (max-width: 630px) {
  figure {
    width: 200px;
    height: 130px;
  }

  figure .wrapper {
    width: 200px;
    height: 130px;
  }

  figure figcaption {
    width: 200px;
    height: 130px;
    padding: 10px 5px 10px 20px;
  }

  figure figcaption::before {
    position: absolute;
    top: 10px;
    right: 5px;
    bottom: 10px;
    left: 100%;
    border-left: 3px solid rgba(255, 255, 255, 0.8);
  }

  figure:hover figcaption::before {
    left: 10px;
  }

  figure h2 {
    font-size: medium;
  }

  figure h2 span {
    font-weight: 200;
  }

  figure p {
    font-size: small;
    font-weight: 200;
  }
}
</style>
